import React, { useEffect, useState } from "react";
import { AlertCircle, Timer, Wifi, Save, Lock, List, Clock } from "lucide-react";
import Lang from "../../config/lang";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { baseUrl, loadData } from "../../config/data";
import SectionPage from "./section";
import QuestionsPage from "./questions"
import SuccessPage from "./success";
import { getQueryParam } from "../../config/helper";

function InfoRow2({ icon, title, text }) {
    return (
        <div className="flex flex-col flex-1 items-start gap-3 bg-[#F0F2F480] h-15 md:h-20 p-3 rounded-lg">
            <p className="text-[11px] md:text-sm text-gray-500">
                {title}
            </p>
            <h2 className="text-[12px] md:text-xl text-gray-900">{text}</h2>
        </div>
    );
}

export default function Assessment() {
    const hasRun = React.useRef(false);
    const [questionCategories, setQuestionCategories] = useState({});
    const [totalDuration, setTotalDuration] = useState(0);
    const [totalQuestion, setTotalQuestion] = useState(0);
    const [pretestStatusText, setPretestStatusText] = useState("-");
    const [isPretestCompleted, setIsPretestCompleted] = useState(false);
    const [pretestTerminalState, setPretestTerminalState] = useState("");
    const [pretestToken, setPretestToken] = useState(getQueryParam("token") || '');

    const [currentScreen, setCurrentScreen] = useState(null);
    const { lang } = useSelector((state) => state.globalReducer);
    const navigate = useNavigate();

    const loadAssessmentData = async () => {
        try {
            if (!pretestToken) {
                navigate(`/${lang}/my-application`);
                return;
            }

            const [questionCategoryResponse, candidateResponse] = await Promise.all([
                loadData({
                    url: `QuestionCategories`,
                    params: [{ title: "filter", value: `pretestToken:${pretestToken}` }],
                }),
                loadData({
                    url: `Candidates/Pretest`,
                    params: [{ title: "token", value: pretestToken }],
                }),
            ]);

            const candidateData = candidateResponse?.data ?? candidateResponse?.Data ?? null;
            const isPassHardFilter =
                candidateData?.isPassHardFilter ?? candidateData?.IsPassHardFilter;
            const hasPretest =
                candidateData?.hasPretest ?? candidateData?.HasPretest ?? false;
            const pretestExpiredAt =
                candidateData?.pretestExpiredAt ?? candidateData?.PretestExpiredAt;
            const pretestExpiredAtTime = pretestExpiredAt
                ? new Date(pretestExpiredAt).getTime()
                : Number.NaN;
            const isPretestExpired =
                Number.isFinite(pretestExpiredAtTime) &&
                pretestExpiredAtTime < Date.now();
            const isPassPretest =
                candidateData?.isPassPretest ?? candidateData?.IsPassPretest;
            const hasPretestResult =
                candidateData?.technicalTestScore != null ||
                candidateData?.TechnicalTestScore != null ||
                candidateData?.cognitiveTestScore != null ||
                candidateData?.CognitiveTestScore != null ||
                candidateData?.behaviourTestScore != null ||
                candidateData?.BehaviourTestScore != null ||
                candidateData?.totalTestScore != null ||
                candidateData?.TotalTestScore != null;
            const assessmentLocal = sessionStorage.getItem("assessmentLocal");
            let sessionID = null;
            let shouldTerminatePretest = false;
            if (assessmentLocal) {
                try {
                    const assessmentData = JSON.parse(assessmentLocal);
                    if (assessmentData?.pretestToken !== pretestToken) {
                        sessionStorage.removeItem("assessmentLocal");
                        sessionStorage.removeItem("sectionNumber");
                        sessionID = null;
                        shouldTerminatePretest = false;
                    } else {
                        sessionID = assessmentData?.sessionID ?? assessmentData?.SessionID;
                        shouldTerminatePretest = !!sessionID;
                    }
                } catch (error) {
                    sessionID = null;
                    shouldTerminatePretest = false;
                }
            }

            if (
                !candidateData ||
                isPassHardFilter !== true ||
                hasPretest !== true
            ) {
                sessionStorage.removeItem("assessmentLocal");
                sessionStorage.removeItem("sectionNumber");
                navigate(`/${lang}/my-application`);
                return;
            }

            if (shouldTerminatePretest) {
                await fetch(
                    `${baseUrl}/TestSessions/${sessionID}/Terminate?token=${encodeURIComponent(pretestToken)}`,
                    { method: "POST" }
                ).catch(() => { });
                sessionStorage.removeItem("assessmentLocal");
                sessionStorage.removeItem("sectionNumber");
                sessionStorage.removeItem(`pretestTerminated-${sessionID}`);
                setPretestTerminalState("completed");
                setPretestStatusText("Completed");
                setIsPretestCompleted(true);
                setCurrentScreen(3);
                return;
            }

            if (isPassPretest != null || hasPretestResult) {
                sessionStorage.removeItem("assessmentLocal");
                sessionStorage.removeItem("sectionNumber");
                setPretestTerminalState("completed");
                setPretestStatusText("Completed");
                setIsPretestCompleted(true);
                setCurrentScreen(3);
                return;
            }

            if (isPretestExpired) {
                sessionStorage.removeItem("assessmentLocal");
                sessionStorage.removeItem("sectionNumber");
                setPretestTerminalState("expired");
                setPretestStatusText("Expired");
                setIsPretestCompleted(true);
                setCurrentScreen(3);
                return;
            }

            const resData = questionCategoryResponse?.data;

            if (!Array.isArray(resData) || resData.length === 0) {
                sessionStorage.removeItem("assessmentLocal");
                sessionStorage.removeItem("sectionNumber");
                navigate(`/${lang}/my-application`);
                return;
            }

            setQuestionCategories(resData);
            const totalDuration = resData?.reduce((sum, item) => sum + item.duration, 0);
            setTotalDuration(totalDuration);
            const totalQuestions = resData?.reduce((sum, item) => sum + item.totalQuestions, 0);
            setTotalQuestion(totalQuestions);
            setPretestStatusText("-");
            setIsPretestCompleted(false);
            setPretestTerminalState("");

            setCurrentScreen(assessmentLocal ? 1 : 0);
        } catch (error) {
            sessionStorage.removeItem("assessmentLocal");
            sessionStorage.removeItem("sectionNumber");
            navigate(`/${lang}/my-application`);
            console.log('ERROR1:', error)
        }
    };


    useEffect(() => {
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    useEffect(() => {
        const handleExitPretest = () => {
            const assessmentLocal = sessionStorage.getItem("assessmentLocal");
            if (!assessmentLocal) {
                return;
            }

            let sessionID = null;
            try {
                const assessmentData = JSON.parse(assessmentLocal);
                sessionID = assessmentData?.sessionID ?? assessmentData?.SessionID;
                if (assessmentData?.pretestToken !== pretestToken) {
                    return;
                }
            } catch (error) {
                sessionID = null;
            }

            if (!sessionID) {
                return;
            }

            sessionStorage.setItem(`pretestTerminated-${sessionID}`, "true");
            const url = `${baseUrl}/TestSessions/${sessionID}/Terminate?token=${encodeURIComponent(pretestToken)}`;
            if (navigator.sendBeacon) {
                navigator.sendBeacon(url);
                return;
            }

            fetch(url, { method: "POST", keepalive: true }).catch(() => { });
        };

        window.addEventListener("pagehide", handleExitPretest);
        window.addEventListener("popstate", handleExitPretest);

        return () => {
            window.removeEventListener("pagehide", handleExitPretest);
            window.removeEventListener("popstate", handleExitPretest);
        };
    }, []);

    const showPretestCompleted = () => {
        sessionStorage.removeItem("assessmentLocal");
        sessionStorage.removeItem("sectionNumber");
        setPretestTerminalState("completed");
        setPretestStatusText("Completed");
        setIsPretestCompleted(true);
        setCurrentScreen(3);
    };

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;
        loadAssessmentData();
    }, [])

    if (currentScreen == null) {
        return <div className="min-h-screen bg-white" />;
    }

    return (
        currentScreen === 0
            ? <div className="min-h-screen bg-white">
                <div className="border-b border-[#E5E7EB] mb-12">
                    <div className="max-w-4xl mx-auto py-10 px-6">
                        <h1 className="text-[24px] md:text-[32px] font-bold text-gray-900">
                            Pre Assessment
                        </h1>
                        <p className="text-[14px] md:text-[20px] text-gray-500">
                            Complete all sections to finish your pre assessment
                        </p>
                    </div>

                </div>

                <div className="max-w-3xl mx-auto px-6 mb-[100px]">
                    <div className="bg-white rounded-xl shadow-lg border p-8">
                        <div className="flex items-start gap-3 mb-6">
                            <AlertCircle className="text-red-500 w-6 h-6" />
                            <div>
                                <h2 className="text-[14px] md:text-xl text-gray-900">
                                    Important Notes
                                </h2>
                                <p className="text-[12px] md:text-sm text-gray-500">
                                    Please read carefully before starting
                                </p>
                            </div>
                        </div>

                        <div className="space-y-3">

                            <InfoRow
                                icon={<img src="/asset/icon/time.svg" className="w-[25px]" />}
                                text="Each section has its own timer"
                            />
                            <InfoRow
                                icon={<Wifi className="text-red-500" />}
                                text="Make sure your internet connection stays stable during the test"
                            />
                            <InfoRow
                                icon={<Save className="text-red-500" />}
                                text="Your answers will be saved automatically"
                            />
                            <InfoRow
                                icon={<Lock className="text-red-500" />}
                                text="You cannot return to previous questions after submitting"
                            />
                        </div>
                        <div className="mt-10 flex flex-wrap gap-3 md:flex-nowrap">
                            <InfoRow2
                                icon={<img src="/asset/icon/file_red.svg" />}
                                title='Total Section'
                                text={(questionCategories ?? []).length}
                            />

                            <InfoRow2
                                icon={<Clock className="text-red-500" />}
                                title={'Total Questions'}
                                text={totalQuestion}
                            />
                            <InfoRow2
                                icon={<Clock className="text-red-500" />}
                                title={'Estimated Total'}
                                text={`${totalDuration} Minutes`}
                            />
                            <InfoRow2
                                icon={<AlertCircle className="text-red-500" />}
                                title={"Pre Assessment Status"}
                                text={pretestStatusText}
                            />
                        </div>
                    </div>
                </div>
                <div className="fixed bottom-0 w-full bg-white py-6 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                    <div className="max-w-3xl mx-auto px-6 flex justify-end">

                        <button
                            className="group flex flex-row right-0 bg-[#EA2427] text-white border border-[#EA2427]
                        hover:bg-white hover:text-[#EA2427]
                        rounded-full py-1 px-3 transition-[1s] font-bold md:mr-2"
                            onClick={() => {
                                if (isPretestCompleted) {
                                    navigate(`/${lang}/my-application`);
                                    return;
                                }

                                setCurrentScreen(1)
                            }}
                        >
                            <span>{isPretestCompleted ? Lang(lang, 'back') : "Continue"}</span>
                            <img
                                src="/asset/icon/arrow_right_white.svg"
                                className="ml-3 w-[20px] group-hover:hidden"
                            />
                            <img
                                src="/asset/icon/arrow_right_red.svg"
                                className="ml-3 w-[20px] hidden group-hover:block"
                            />
                        </button>
                    </div>
                </div>
            </div>
            : currentScreen === 1
                ? <SectionPage setCurrentScreen={setCurrentScreen} onPretestCompleted={showPretestCompleted} pretestToken={pretestToken} />
                : currentScreen === 2
                    ? <QuestionsPage setCurrentScreen={setCurrentScreen} onPretestCompleted={showPretestCompleted} pretestToken={pretestToken} />
                    : <SuccessPage status={pretestTerminalState || "finished"} />
    );
}

function InfoRow({ icon, text }) {
    return (
        <div className="flex items-center gap-3 bg-[#F0F2F480] py-3 px-4 rounded-lg">
            <span className="w-6 h-6 flex items-center justify-center">{icon}</span>
            <p className="text-[12px] md:text-sm text-gray-700">{text}</p>
        </div>
    );
}
